/**
 * Headless HepCURE badge → PNG renderer.
 *
 * Draws a full CR80-portrait badge on an off-screen canvas WITHOUT relying
 * on any live DOM node. This lets any caller (the badge editor, the admin
 * registry's per-row Download button, a future bulk-export flow) produce
 * a pixel-identical 4K PNG entirely inside the click handler that started
 * it — critical on iOS Safari, which only lets programmatic downloads
 * happen inside an active user-gesture window.
 *
 * Return value is a Blob suitable for `URL.createObjectURL(...)`.
 */
import { renderToStaticMarkup } from "react-dom/server";
import { QRCodeSVG } from "qrcode.react";
import { createElement } from "react";

// --- Badge layout constants (must match `Badge.jsx`) ----------------------

export const BADGE_W = 400;
const BADGE_ASPECT = 3.375 / 2.125;
export const BADGE_H = Math.round(BADGE_W * BADGE_ASPECT); // 636

// Photo section
const PHOTO_PAD_TOP = 26;
const PHOTO_PAD_BOTTOM = 12;
const PHOTO_PAD_SIDE = 20;
const PHOTO_MAX_W = 280;
const PHOTO_MAX_H = 300;
const PHOTO_MIN_ASPECT = 0.85;
const PHOTO_MAX_ASPECT = 1.4;
const PHOTO_BORDER = 3;

// Top band
const TOP_BAND_H = 105;
const LOGO_H = 40;
const LOGO_PAD_BOTTOM = 16;
const LANYARD_TOP = 16;
const LANYARD_W = 80;
const LANYARD_H = 14;

// Info row (name / dept + QR)
const INFO_PAD_TOP = 8;
const INFO_PAD_BOTTOM = 12;
const INFO_PAD_SIDE = 20;
const INFO_GAP = 14;
const QR_INNER = 68; // The QRCodeSVG size
const QR_PAD = 5;
const QR_BORDER = 1;
const QR_BOX = QR_INNER + (QR_PAD + QR_BORDER) * 2; // ~80

// Bottom banner
const BANNER_PAD_TOP = 12;
const BANNER_PAD_BOTTOM = 16;
const BANNER_BORDER_TOP = 2;
const BANNER_SCAN_H = 12; // fs 9 × line-height ≈ 12
const BANNER_ROLE_H = 24; // fs 21 × line-height 1.15 ≈ 24
const BANNER_GAP = 6;
const BANNER_H =
  BANNER_BORDER_TOP +
  BANNER_PAD_TOP +
  BANNER_SCAN_H +
  BANNER_GAP +
  BANNER_ROLE_H +
  BANNER_PAD_BOTTOM;

// Font stack matches the badge preview (Inter is not loaded, so fall through
// to system-ui / Segoe UI which is what the DOM actually renders in).
const BADGE_FONT = "'Inter', system-ui, -apple-system, 'Segoe UI', sans-serif";

// --- Helpers --------------------------------------------------------------

const roundedPath = (ctx, x, y, w, h, r) => {
  const rr = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.arcTo(x + w, y, x + w, y + h, rr);
  ctx.arcTo(x + w, y + h, x, y + h, rr);
  ctx.arcTo(x, y + h, x, y, rr);
  ctx.arcTo(x, y, x + w, y, rr);
  ctx.closePath();
};

const loadImage = (src, crossOrigin = null) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    if (crossOrigin) img.crossOrigin = crossOrigin;
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });

const canvasToBlob = (canvas) =>
  new Promise((resolve) => {
    if (canvas.toBlob) {
      canvas.toBlob((b) => resolve(b), "image/png");
    } else {
      const url = canvas.toDataURL("image/png");
      const [meta, b64] = url.split(",");
      const mime = meta.match(/:(.*?);/)?.[1] || "image/png";
      const bin = atob(b64);
      const arr = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
      resolve(new Blob([arr], { type: mime }));
    }
  });

// --- Public API -----------------------------------------------------------

/**
 * Render the badge to a PNG Blob.
 *
 * @param {object} opts
 * @param {object} opts.employee - { id_number, name, department, role,
 *                                   issued, expires }
 * @param {string} opts.photoUrl - absolute or root-relative URL of the
 *                                 employee photo (may be empty)
 * @param {string} opts.verifyOrigin - origin used for the QR payload,
 *                                     e.g. `https://hepcure.ca`
 * @param {{zoom:number, offsetX:number, offsetY:number}} [opts.transform] -
 *   optional photo transform (used only when called from the editor with
 *   an unsaved zoom/pan state; admin re-renders always pass zoom=1, off=0
 *   because the persisted photo is already framed).
 * @param {number} [opts.pixelRatio] - export scale multiplier (default 8×)
 * @param {string} [opts.logoUrl] - white HepCURE logo (default
 *                                  `/brand/hepcure-logo-white.png`)
 * @returns {Promise<Blob>}
 */
export async function renderBadgeBlob({
  employee,
  photoUrl,
  verifyOrigin,
  transform = { zoom: 1, offsetX: 0, offsetY: 0 },
  pixelRatio = 8,
  logoUrl = "/brand/hepcure-logo-white.png",
}) {
  // Wait for webfonts so canvas text uses the same face as the DOM preview.
  if (document.fonts?.ready) {
    try { await document.fonts.ready; } catch { /* ignore */ }
  }

  // Load photo (may be missing) and logo in parallel.
  const [photoImg, logoImg] = await Promise.all([
    photoUrl ? loadImage(photoUrl).catch(() => null) : Promise.resolve(null),
    loadImage(logoUrl).catch(() => null),
  ]);

  // Compute photo-frame dimensions from the natural aspect ratio, mirroring
  // the badge preview's logic in Badge.jsx.
  let frameW = PHOTO_MAX_W;
  let frameH = PHOTO_MAX_W;
  if (photoImg && photoImg.naturalWidth && photoImg.naturalHeight) {
    const rawA = photoImg.naturalWidth / photoImg.naturalHeight;
    const a = Math.min(PHOTO_MAX_ASPECT, Math.max(PHOTO_MIN_ASPECT, rawA));
    frameW = PHOTO_MAX_W;
    frameH = PHOTO_MAX_W / a;
    if (frameH > PHOTO_MAX_H) {
      frameH = PHOTO_MAX_H;
      frameW = PHOTO_MAX_H * a;
    }
  } else {
    frameH = PHOTO_MAX_W; // square placeholder
  }

  // Photo section height
  const photoSectionH = PHOTO_PAD_TOP + frameH + PHOTO_PAD_BOTTOM;

  // Vertical layout: top-band | photo | info-row (fills) | bottom-banner
  const infoRowH = Math.max(
    INFO_PAD_TOP + QR_BOX + INFO_PAD_BOTTOM, // min height (QR sets the floor)
    BADGE_H - TOP_BAND_H - photoSectionH - BANNER_H,
  );
  const bannerY = BADGE_H - BANNER_H;

  // -----------------------------------------------------------------------
  // Set up canvas
  // -----------------------------------------------------------------------
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(BADGE_W * pixelRatio);
  canvas.height = Math.round(BADGE_H * pixelRatio);
  const ctx = canvas.getContext("2d");
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.scale(pixelRatio, pixelRatio);

  // Card background (white, rounded 22) + clip
  ctx.fillStyle = "#ffffff";
  roundedPath(ctx, 0, 0, BADGE_W, BADGE_H, 22);
  ctx.fill();
  ctx.save();
  roundedPath(ctx, 0, 0, BADGE_W, BADGE_H, 22);
  ctx.clip();

  // -----------------------------------------------------------------------
  // Top navy band
  // -----------------------------------------------------------------------
  const bandGrad = ctx.createLinearGradient(0, 0, 0, TOP_BAND_H);
  bandGrad.addColorStop(0, "#1F4E79");
  bandGrad.addColorStop(1, "#183F63");
  ctx.fillStyle = bandGrad;
  ctx.fillRect(0, 0, BADGE_W, TOP_BAND_H);

  // Lanyard slot
  ctx.fillStyle = "#ffffff";
  roundedPath(
    ctx,
    (BADGE_W - LANYARD_W) / 2,
    LANYARD_TOP,
    LANYARD_W,
    LANYARD_H,
    8,
  );
  ctx.fill();

  // Logo (bottom-aligned with padding-bottom:16)
  if (logoImg) {
    const logoRatio = logoImg.naturalWidth / logoImg.naturalHeight;
    const lw = LOGO_H * logoRatio;
    const lh = LOGO_H;
    ctx.drawImage(
      logoImg,
      (BADGE_W - lw) / 2,
      TOP_BAND_H - LOGO_PAD_BOTTOM - lh,
      lw,
      lh,
    );
  }

  // -----------------------------------------------------------------------
  // Photo frame
  // -----------------------------------------------------------------------
  const frameX = (BADGE_W - frameW) / 2;
  const frameY = TOP_BAND_H + PHOTO_PAD_TOP;

  // Frame background (inside the border)
  ctx.fillStyle = "#f1f5f9";
  roundedPath(
    ctx,
    frameX + PHOTO_BORDER,
    frameY + PHOTO_BORDER,
    frameW - PHOTO_BORDER * 2,
    frameH - PHOTO_BORDER * 2,
    3,
  );
  ctx.fill();

  // Photo (with optional zoom/pan transform)
  if (photoImg) {
    const innerW = frameW - PHOTO_BORDER * 2;
    const innerH = frameH - PHOTO_BORDER * 2;

    ctx.save();
    // Clip to inner rounded rect
    roundedPath(
      ctx,
      frameX + PHOTO_BORDER,
      frameY + PHOTO_BORDER,
      innerW,
      innerH,
      3,
    );
    ctx.clip();

    // Move origin to inner top-left, then apply CSS transform:
    //   translate(offsetX, offsetY) scale(zoom); origin center center;
    ctx.translate(frameX + PHOTO_BORDER, frameY + PHOTO_BORDER);
    ctx.translate(transform.offsetX, transform.offsetY);
    ctx.translate(innerW / 2, innerH / 2);
    ctx.scale(transform.zoom, transform.zoom);
    ctx.translate(-innerW / 2, -innerH / 2);

    const nw = photoImg.naturalWidth;
    const nh = photoImg.naturalHeight;
    const imgA = nw / nh;
    const frameA = innerW / innerH;
    let dispW;
    let dispH;
    if (imgA > frameA) {
      dispW = innerW;
      dispH = innerW / imgA;
    } else {
      dispH = innerH;
      dispW = innerH * imgA;
    }
    ctx.drawImage(
      photoImg,
      0, 0, nw, nh,
      (innerW - dispW) / 2, (innerH - dispH) / 2, dispW, dispH,
    );
    ctx.restore();
  }

  // Frame border (drawn on top for crisp edges)
  ctx.strokeStyle = "#1F4E79";
  ctx.lineWidth = PHOTO_BORDER;
  roundedPath(
    ctx,
    frameX + PHOTO_BORDER / 2,
    frameY + PHOTO_BORDER / 2,
    frameW - PHOTO_BORDER,
    frameH - PHOTO_BORDER,
    3,
  );
  ctx.stroke();

  // -----------------------------------------------------------------------
  // Info row (name/dept text + QR)
  // -----------------------------------------------------------------------
  const infoRowY = TOP_BAND_H + photoSectionH;

  // Content is centered horizontally with a gap between text and QR.
  // Measure text widths to size the text block; QR is fixed.
  ctx.font = `800 22px ${BADGE_FONT}`;
  const nameW = ctx.measureText(employee.name || "").width;
  ctx.font = `700 13px ${BADGE_FONT}`;
  const deptW = ctx.measureText(employee.department || "").width;
  const textBlockW = Math.max(nameW, deptW);
  const totalW = textBlockW + INFO_GAP + QR_BOX;
  // Fit into available width (info row side padding leaves BADGE_W - 40).
  const availW = BADGE_W - INFO_PAD_SIDE * 2;
  const groupX = INFO_PAD_SIDE + Math.max(0, (availW - totalW) / 2);

  // Vertical center inside info row
  const infoContentH = QR_BOX; // QR is the tallest element
  const infoContentY = infoRowY + (infoRowH - infoContentH) / 2;

  // -- Name + Dept (centered on textBlockW column) --
  const textCX = groupX + textBlockW / 2;
  // Name
  ctx.save();
  ctx.font = `800 22px ${BADGE_FONT}`;
  ctx.fillStyle = "#1F4E79";
  ctx.textAlign = "center";
  ctx.textBaseline = "alphabetic";
  if ("letterSpacing" in ctx) ctx.letterSpacing = "-0.22px";
  // Position: name sits vertically at ~QR center - 6 (upper half)
  ctx.fillText(
    employee.name || "",
    textCX,
    infoContentY + QR_BOX / 2 - 4,
  );
  ctx.restore();
  // Dept
  ctx.save();
  ctx.font = `700 13px ${BADGE_FONT}`;
  ctx.fillStyle = "#0f172a";
  ctx.textAlign = "center";
  ctx.textBaseline = "alphabetic";
  if ("letterSpacing" in ctx) ctx.letterSpacing = "0.52px";
  ctx.fillText(
    employee.department || "",
    textCX,
    infoContentY + QR_BOX / 2 + 16,
  );
  ctx.restore();

  // -- QR box --
  const qrX = groupX + textBlockW + INFO_GAP;
  const qrY = infoContentY;
  ctx.fillStyle = "#ffffff";
  roundedPath(ctx, qrX, qrY, QR_BOX, QR_BOX, 8);
  ctx.fill();
  ctx.strokeStyle = "#cbd5e1";
  ctx.lineWidth = 1;
  roundedPath(ctx, qrX + 0.5, qrY + 0.5, QR_BOX - 1, QR_BOX - 1, 8);
  ctx.stroke();

  // Generate the QR SVG via react-dom/server so this helper works entirely
  // headless. QRCodeSVG produces plain SVG paths (no external refs) so it
  // rasterizes cleanly through an <img> tag with no CORS pitfalls.
  const verifyUrl = `${verifyOrigin}/verify?id=${encodeURIComponent(
    employee.id_number || "",
  )}`;
  const svgMarkup = renderToStaticMarkup(
    createElement(QRCodeSVG, {
      value: verifyUrl,
      size: QR_INNER,
      level: "H",
      marginSize: 2,
      fgColor: "#0f172a",
      bgColor: "#ffffff",
      xmlns: "http://www.w3.org/2000/svg",
    }),
  );
  // Ensure the xmlns attr is present (some renderers omit it) — required
  // for the SVG to be usable as an <img src="data:image/svg+xml,...">.
  const svgWithXmlns = svgMarkup.includes("xmlns=")
    ? svgMarkup
    : svgMarkup.replace("<svg ", '<svg xmlns="http://www.w3.org/2000/svg" ');
  const svgUrl =
    "data:image/svg+xml;charset=utf-8," + encodeURIComponent(svgWithXmlns);
  try {
    const qrImg = await loadImage(svgUrl);
    ctx.drawImage(
      qrImg,
      qrX + QR_PAD + QR_BORDER,
      qrY + QR_PAD + QR_BORDER,
      QR_INNER,
      QR_INNER,
    );
  } catch { /* skip QR on failure */ }

  // -----------------------------------------------------------------------
  // Bottom banner
  // -----------------------------------------------------------------------
  const bannerGrad = ctx.createLinearGradient(
    0, bannerY + BANNER_H, 0, bannerY,
  );
  bannerGrad.addColorStop(0, "#1F4E79");
  bannerGrad.addColorStop(1, "#255F92");
  ctx.fillStyle = bannerGrad;
  ctx.fillRect(0, bannerY, BADGE_W, BANNER_H);
  ctx.fillStyle = "#6BA292";
  ctx.fillRect(0, bannerY, BADGE_W, BANNER_BORDER_TOP);

  // Scan hint
  ctx.save();
  ctx.font = `600 9px ${BADGE_FONT}`;
  ctx.fillStyle = "rgba(255,255,255,0.75)";
  ctx.textAlign = "center";
  ctx.textBaseline = "top";
  if ("letterSpacing" in ctx) ctx.letterSpacing = "1.62px";
  ctx.fillText(
    "SCAN QR TO VERIFY · hepcure.ca/verify",
    BADGE_W / 2,
    bannerY + BANNER_BORDER_TOP + BANNER_PAD_TOP,
  );
  ctx.restore();

  // Role
  ctx.save();
  ctx.font = `800 21px ${BADGE_FONT}`;
  ctx.fillStyle = "#ffffff";
  ctx.textAlign = "center";
  ctx.textBaseline = "top";
  if ("letterSpacing" in ctx) ctx.letterSpacing = "3.78px";
  ctx.fillText(
    (employee.role || "").toUpperCase(),
    BADGE_W / 2,
    bannerY + BANNER_BORDER_TOP + BANNER_PAD_TOP + BANNER_SCAN_H + BANNER_GAP,
  );
  ctx.restore();

  ctx.restore(); // release the card clip

  return canvasToBlob(canvas);
}

/**
 * Trigger a download of the badge PNG. MUST be called synchronously from a
 * user-gesture event handler (e.g. onClick) so iOS Safari permits the file
 * save. The rendering/decoding is done inline via `await` inside the same
 * handler — the anchor `.click()` is fired as soon as the blob is ready.
 */
export async function downloadBadgePng(opts) {
  const blob = await renderBadgeBlob(opts);
  const url = URL.createObjectURL(blob);
  const filename = `hepcure-badge-${(opts.employee.name || "employee")
    .replace(/\s+/g, "-")
    .toLowerCase()}.png`;
  const link = document.createElement("a");
  link.download = filename;
  link.href = url;
  link.rel = "noopener";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(url), 4000);
  return blob;
}
